import type { components } from './tipos/plataforma';

/** Error de la API en formato RFC 9457 (esquema `Problem` del contrato, igual en todos los módulos). */
export type Problema = components['schemas']['Problem'];

/** Código estable del error: es lo que mira el panel para decidir qué mostrar; el texto es para personas. */
export type CodigoProblema = Problema['code'];

/** Error que lanza el cliente de la API cuando la respuesta no es correcta. */
export class ErrorApi extends Error {
    constructor(
        public readonly problema: Problema,
        /** Segundos que pide esperar la API (`Retry-After`, en `rate_limited`). */
        public readonly reintentarEn: number | null = null,
    ) {
        super(problema.detail ?? problema.title);
        this.name = 'ErrorApi';
    }

    get codigo(): CodigoProblema {
        return this.problema.code;
    }

    get estado(): number {
        return this.problema.status;
    }

    /** Errores por campo (`errors`), para marcarlos en el formulario. */
    get errores(): NonNullable<Problema['errors']> {
        return this.problema.errors ?? [];
    }
}

/**
 * Todos los códigos de `Problem.code` del contrato: un `Record` obliga a tenerlos todos (si el contrato añade uno, la
 * comprobación de tipos falla). Un código que faltara aquí convertía el error en un `internal_error` genérico.
 */
const TODOS_LOS_CODIGOS: Record<CodigoProblema, true> = {
    validation_failed: true, unauthenticated: true, two_factor_required: true, two_factor_confirmation_required: true, reauthentication_required: true,
    email_not_verified: true, permission_denied: true, sign_in_method_not_allowed: true, invitation_email_mismatch: true, tenant_mismatch: true, read_only_mode: true,
    entitlement_required: true, not_found: true, conflict: true, confirmation_required: true, commitments_pending: true, whatsapp_account_limit_reached: true, slug_change_limit_reached: true,
    api_key_type_not_revocable: true, capacity_unavailable: true, hold_expired: true, change_window_closed: true, version_conflict: true, idempotency_conflict: true,
    captcha_required: true, code_invalid: true, recent_verification_required: true, phone_required: true,
    // Códigos propios de los módulos en la lista común (G813, G839, G856): la pantalla decide el mensaje mirando solo `code`.
    privacy_notice_not_allowed: true, signup_link_inactive: true, signup_list_closed: true, signup_full: true, signup_event_over: true,
    sale_mode_locked: true, instagram_not_connected: true, guarantee_requires_link: true, deposit_with_guarantee_requires_link: true,
    change_expired: true, nominative_locked: true, door_quota_below_assigned: true, device_required: true,
    // G1080: reenvío del pase sin contacto del cliente (`409`), distinto de un pase anulado (`conflict`).
    no_contact: true,
    rate_limited: true, provider_error: true, internal_error: true,
};
const CODIGOS: readonly string[] = Object.keys(TODOS_LOS_CODIGOS);

/** ¿Tiene forma de `Problem` del contrato? */
export function esProblema(valor: unknown): valor is Problema {
    if (typeof valor !== 'object' || valor === null) {
        return false;
    }
    const v = valor as Record<string, unknown>;
    return typeof v.type === 'string'
        && typeof v.title === 'string'
        && typeof v.status === 'number'
        && typeof v.code === 'string'
        && CODIGOS.includes(v.code);
}

/** Para respuestas de error que no traen un `Problem` (proxy caído, HTML de un error del servidor…). */
export function problemaGenerico(estado: number): Problema {
    return {
        type: 'https://api.reservaria.app/errors/internal_error',
        title: 'Error inesperado del servidor',
        status: estado,
        code: 'internal_error',
    };
}
